import {
  type TestimonialsType,
  type SocialMediaType,
  type HeaderLinkType,
  type MobileAppMetaDataType,
  type LegalDataType,
  type AnalyticsType,
  type ConfigDataType,
} from './PhotonRequest';

export type PageSchemaType = Record<string, CustomPageType>;
export type PageTemplatesType =
  | 'losPollosHermanos'
  | 'cafeShirley'
  | 'pizzaCafe'
  | 'default';

export enum PageTemplates {
  LosPollosHermanos = 'losPollosHermanos',
  CafeShirley = 'cafeShirley',
  PizzaCafe = 'pizzaCafe',
  Default = 'default',
}

export type RedirectType = { from: string; to: string; status: 301 | 302 };

export type MenuLinkType = {
  NavigationLinkTitle: string;
  NavigationLinkIndex: number;
  LinkDisplaySection: 'header' | 'footer';
  url: string;
};

export type headHTMLObjType = {
  customizer?: Record<string, string>;
  styles?: Array<{
    text?: string;
  }>;
  links?: Array<{ rel?: string; href: string }>;
  scripts?: Array<{ src?: string; text?: string }>;
  meta?: Array<{ name?: string; content?: string }>;
  facebookMeta?: string;
  googleMeta?: string;
  gaId?: string;
  fonts?: string[];
};

export type CustomPageSchemaType = {
  locationPageEnabled: boolean;
  name: string;
  pages: Record<string, CustomPageType>;
  headHTMLObj?: headHTMLObjType;
  globalCSS?: string;
  headerLinks?: HeaderLinkType;
  socialMedia?: SocialMediaType;
  template?: PageTemplatesType;
  mobileApps?: MobileAppMetaDataType;
  legal?: LegalDataType;
  analytics?: AnalyticsType;
  config?: ConfigDataType;
  redirects?: RedirectType[];
  menuLinks?: MenuLinkType[];
};

export enum PageType {
  AboutUsPage = 'AboutUsPage',
  HQContactPage = 'HQContactPage',
  EmptyPage = 'EmptyPage',
  EmptyNoHeaderPage = 'EmptyNoHeaderPage',
  IndexPage = 'IndexPage',
}

export enum V2PageType {
  AboutUsPage = 'AboutUsPage',
  HQContactPage = 'HQContactPage',
  EmptyPage = 'EmptyPage',
  EmptyNoHeaderPage = 'EmptyNoHeaderPage',
  LandingPage = 'LandingPage',
  ContactPage = 'ContactPage',
  ReservationsPage = 'ReservationsPage',
}

export type CustomPageType =
  | HqContactPageType
  | AboutUsPageType
  | EmptyPageType
  | EmptyNoHeaderPage
  | IndexPageType;

export type V2EmptyPageType = {
  html?: string;
  PageType: V2PageType.EmptyPage;
};

export type V2EmptyNoHeaderPage = Omit<V2EmptyPageType, 'PageType'> & {
  header?: boolean;
  PageType: V2PageType.EmptyNoHeaderPage;
};

export type AboutUsPageBlockType = {
  title: string;
  description: string;
  images: string[];
  actionButton?: {
    label: string;
    url: string;
  };
};

export type V2AboutUsPageType = {
  PageType: V2PageType.AboutUsPage;
  title?: string;
  subTitle?: string;
  pageCSS?: string;
  blocks: AboutUsPageBlockType[];
};

export type V2HqContactPageType = {
  PageType: V2PageType.HQContactPage;
  CompanyName?: string;
  Email?: string;
  CompanyRegistrationNumber?: number;
  AddressLine2?: string;
  AddressLine1?: string;
  Phone?: string;
  CeoFullName?: string;
  TaxNumber?: string;
  VatNumber?: string;
  RegisterCourt?: string;
};

export type V2ContactPageType = {
  PageType?: V2PageType.ContactPage | V2PageType.ReservationsPage;
  title?: string;
  subTitle?: string;
  pageCSS?: string;
  hasReservations?: boolean;
  hasContactForm?: boolean;
  hasLocations?: boolean;
};

export type V2CustomPageType =
  | V2EmptyPageType
  | V2EmptyNoHeaderPage
  | LandingPageType
  | V2AboutUsPageType
  | V2HqContactPageType
  | V2ContactPageType;

export type V2HighlightType = {
  enabled?: boolean;
  title?: string;
  subTitle?: string;
  description: string;
  actionButton?: {
    label: string;
    url: string;
  };
  images: string[];
};

export type LandingPageType = {
  PageType: V2PageType.LandingPage;
  backgroundImages: string[];
  highlight: V2HighlightType;
  testimonialsEnabled?: boolean;
  galleryEnabled?: boolean;
  mobileAppsEnabled?: boolean;
  hasLocations?: boolean;
};

export type CustomPageGenericType = {
  NavigationLinkTitle?: string; // Formerly Title
  NavigationLinkIndex?: number; // Formerly Linkindex
  LinkDisplaySection?: 'header' | 'footer' | 'none';
  pageStyles?: string;
  enabled: boolean;
};

export type HqContactPageType = {
  PageType: PageType.HQContactPage;
  CompanyName?: string;
  Email?: string;
  CompanyRegistrationNumber?: number;
  AddressLine2?: string;
  AddressLine1?: string;
  Phone?: string;
  CeoFullName?: string;
  TaxNumber?: string;
  VatNumber?: string;
  RegisterCourt?: string;
} & CustomPageGenericType;

export type AboutUsPageType = {
  PageType: PageType.AboutUsPage;
  title?: string; // Formally h1
  subtitle?: string;
  left: {
    title: string;
    description: string;
    images: string[];
  };
  right: {
    title: string;
    description: string;
    images: string[];
  };
} & CustomPageGenericType;

export type EmptyPageType = {
  html?: string;
  PageType: PageType.EmptyPage;
} & CustomPageGenericType;

export type EmptyNoHeaderPage = Omit<EmptyPageType, 'PageType'> & {
  header?: boolean;
  PageType: PageType.EmptyNoHeaderPage;
} & CustomPageGenericType;

export type IndexPageType = {
  PageType: PageType.IndexPage;
  testimonials: TestimonialsType;
  aboutUs: AboutUsPageType;
  openingHoursEnabled: boolean;
  openingHoursImage: string;
  showMenu: boolean;
  voucherPopupEnabled: boolean;
  showMap: boolean;
  showGallery: boolean;
  showTestimonials: boolean;
  contactFormEnabled: boolean;
  footer: boolean;
  galleryImages: string[];
  mainSliderImages: string[];
  showAddressMainSlider: boolean;
  voucher?: {
    isPercent: boolean;
    amount: number;
  };
  reservations?: {
    enabled: boolean;
    daysAhead: number;
  };
} & CustomPageGenericType;

export type PageLinkType = {
  path: string;
  title: string;
  pageType?: PageType;
};
